import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import Sidebar from "@/components/Sidebar";
import TopTabs from "@/components/TopTabs";
import Dashboard from "@/pages/Dashboard";
import Fields from "@/pages/Fields";
import Yields from "@/pages/Yields";
import Inventory from "@/pages/Inventory";
import Livestock from "@/pages/Livestock";
import Expenses from "@/pages/Expenses";
import Equipment from "@/pages/Equipment";
import Mileage from "@/pages/Mileage";
import FieldPnL from "@/pages/FieldPnL";
import YearCompare from "@/pages/YearCompare";
import Reports from "@/pages/Reports";
import BalanceSheet from "@/pages/BalanceSheet";
import Projections from "@/pages/Projections";
import Maintenance from "@/pages/Maintenance";
import CalendarPage from "@/pages/Calendar";
import Markets from "@/pages/Markets";
import Partners from "@/pages/Partners";
import PartnerInvoices from "@/pages/PartnerInvoices";
import FieldDetail from "@/pages/FieldDetail";
import Hub from "@/pages/Hub";
import BusinessRouter from "@/pages/business/BusinessRouter";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import TeamSettings from "@/pages/TeamSettings";
import Notifications from "@/pages/Notifications";
import YearSummaryPostcard from "@/pages/YearSummaryPostcard";
import FMVWizard from "@/pages/FMVWizard";
import QuickLogFab from "@/components/QuickLogFab";
import OfflineBanner from "@/components/OfflineBanner";
import { YearProvider } from "@/context/YearContext";
import { BusinessProvider, useBusiness } from "@/context/BusinessContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";

function FarmLayout({ children }) {
  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-[#F5F4F0]">
      <Sidebar />
      <main className="flex-1 p-4 sm:p-6 md:p-10 overflow-x-hidden min-w-0">{children}</main>
      <QuickLogFab />
    </div>
  );
}

function HomeRouter() {
  const { setActive } = useBusiness();
  React.useEffect(() => { setActive("farm"); }, [setActive]);
  return (
    <FarmLayout>
      <Routes>
        <Route index element={<Dashboard />} />
        <Route path="fields" element={<Fields />} />
        <Route path="fields/:id" element={<FieldDetail />} />
        <Route path="yields" element={<Yields />} />
        <Route path="inventory" element={<Inventory />} />
        <Route path="livestock" element={<Livestock />} />
        <Route path="expenses" element={<Expenses scopeBusinessId="farm" />} />
        <Route path="equipment" element={<Equipment />} />
        <Route path="mileage" element={<Mileage />} />
        <Route path="maintenance" element={<Maintenance />} />
        <Route path="field-pnl" element={<FieldPnL />} />
        <Route path="partners" element={<Partners />} />
        <Route path="partner-invoices" element={<PartnerInvoices />} />
        <Route path="year-compare" element={<YearCompare />} />
        <Route path="balance-sheet" element={<BalanceSheet />} />
        <Route path="projections" element={<Projections />} />
        <Route path="reports" element={<Reports />} />
        <Route path="year-summary" element={<YearSummaryPostcard />} />
        <Route path="fmv-wizard" element={<FMVWizard />} />
        <Route path="team" element={<TeamSettings />} />
        <Route path="notifications" element={<Notifications />} />
      </Routes>
    </FarmLayout>
  );
}

function BusinessLayout({ businessId, type }) {
  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-[#F5F4F0]">
      <Sidebar />
      <main className="flex-1 p-4 sm:p-6 md:p-10 overflow-x-hidden min-w-0">
        <BusinessRouter businessId={businessId} type={type} />
      </main>
    </div>
  );
}

function GlobalLayout({ children }) {
  return (
    <main className="flex-1 p-4 sm:p-6 md:p-10 overflow-x-hidden min-w-0 bg-[#F5F4F0] min-h-screen">{children}</main>
  );
}

function ProtectedShell() {
  const { user, loading, bootstrapping } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F4F0]" data-testid="auth-loading">
        <div className="text-sm uppercase tracking-[0.2em] font-bold text-[#66645E]">Loading…</div>
      </div>
    );
  }

  if (!user && !bootstrapping) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return (
    <>
      <TopTabs />
      <OfflineBanner />
      <Routes>
        <Route path="/repair/*" element={<BusinessLayout businessId="jk_repair" type="repair" />} />
        <Route path="/trucking/*" element={<BusinessLayout businessId="jk_trucking" type="trucking" />} />
        <Route path="/calendar" element={<GlobalLayout><CalendarPage /></GlobalLayout>} />
        <Route path="/markets" element={<GlobalLayout><Markets /></GlobalLayout>} />
        <Route path="/hub" element={<GlobalLayout><Hub /></GlobalLayout>} />
        <Route path="/*" element={<HomeRouter />} />
      </Routes>
    </>
  );
}

function AppRouter() {
  const location = useLocation();
  // Handle Emergent OAuth callback (session_id in URL hash) BEFORE any auth check
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/*" element={<ProtectedShell />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <YearProvider>
            <BusinessProvider>
              <AppRouter />
              <Toaster position="top-right" toastOptions={{ style: { borderRadius: 0, border: "1px solid #1F1E1B", fontFamily: "IBM Plex Sans" } }} />
            </BusinessProvider>
          </YearProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
